import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { ICategorySFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { ICategoryGridRow } from 'src/hooks/useGetGridCol';
import useGetGuideInfo from 'src/hooks/useGetGuideInfo';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { ICategory, useStores } from 'src/models';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { CategoryFormDialog } from './CategoryFormDialog';

interface ICategoryGridProps {
  searchData: Partial<ICategorySFilter>;
}

export const CategoryGrid = ({ searchData }: ICategoryGridProps) => {
  const { t } = useTranslation();
  const { businessStore, categoryStore } = useStores();
  const { getCategryGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { getCategoryAreas, getTopCategoryAreas } = useGetGuideInfo();
  const { openDialog, closeDialog } = useDialogContext();

  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<ICategory[]>([]);
  // 가이드 토글
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  // 목록 조회
  const getCategoryData = useCallback(async () => {
    const formData = {
      ...searchData,
      page: businessStore.pagination.page,
      size: businessStore.pagination.size,
    };
    await categoryStore.getCategory(formData).then(() => {
      if (categoryStore.categoryTree) {
        const newsGird = categoryStore.categories?.map((category, i) => {
          return { ...category, id: category.categorySid ?? i };
        });
        setGridRows(newsGird as ICategory[]);
      }
    });
  }, [searchData]);

  useEffect(() => {
    getCategoryData();
  }, [getCategoryData]);

  // 등록 버튼: 등록 dialog open
  const handleAddCategory = () => {
    openDialog({
      id: FORM_IDS.CATEGORY,
      formType: FormType.CREATE,
      dialogContent: (
        <CategoryFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<ICategoryGridRow>) => {
    callApiWithLoading(async () => {
      await categoryStore.getDetail(param.row.categorySid!).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.CATEGORY,
            formType: FormType.UPDATE,
            dialogContent: (
              <CategoryFormDialog
                formType={FormType.UPDATE}
                onSubmitSuccess={handleSubmitSuccess}
                selectedData={res.data as ICategory}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.CATEGORY);
    getCategoryData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    businessStore.updatePagination({
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    });
    getCategoryData();
  };

  // 가이드 토글
  const handleGuideToggle = () => {
    setIsGuideOpen(!isGuideOpen);
  };
  return (
    <ContentBox>
      <ContentBoxButtons>
        <ContentButtonCreate onClick={handleAddCategory}>
          {t('common.button.register')}
        </ContentButtonCreate>
        {/* <ContentButtonText onClick={handleGuideToggle}>
          <Iconify icon="mdi:help-circle-outline" />
        </ContentButtonText> */}
      </ContentBoxButtons>
      {/* <Collapse in={isGuideOpen}>
        <ServicePreview>
          <ServiceGuide areas={getTopCategoryAreas()} mode="guide" />
          <ServiceGuide areas={getCategoryAreas()} mode="guide" />
        </ServicePreview>
      </Collapse> */}
      <CDataGridPro
        columns={getCategryGridCol()}
        rows={gridRows}
        rowCount={toJS(businessStore.pagination.totalElements)}
        paginationModel={{
          page: businessStore.pagination.page - 1,
          pageSize: businessStore.pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{ footer: CGridFooter }}
        onRowDoubleClick={handleRowDoubleClick}
        getRowId={(row) => `${row.categoryNm} -${row.categorySid}`} // 고유 ID를 정의
        groupingColDef={{
          headerName: '카테고리 이름',
          headerAlign: 'center',
          align: 'center',
          flex: 3,
        }}
        treeData
        getTreeDataPath={(row) => {
          // row.categoryNm이 트리 구조를 표현하도록 경로 배열 반환
          const path: string[] = [];
          let current = row;
          while (current) {
            path.unshift(current.categoryNm);
            current = gridRows.find((category) => category.categorySid === current.pCategory);
          }
          return path;
        }}
        defaultGroupingExpansionDepth={-1} // 모든 트리를 기본적으로 확장
      />
    </ContentBox>
  );
};
